/**
 * Write & send › common ground (PR 7, Oct 27 2026): the facts of the profile the InMail is sent
 * from. One click reads the profile of whoever is signed in to LinkedIn (the extension opens
 * linkedin.com/in/me in the background — Sofia: "as easy as possible"); ticked or deleted here. A draft names at most one of them, and only when the candidate's own profile has it.
 */
import { useEffect, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taOutreachApi';
import type { SenderFact } from '../../types/taOutreach';
import { captureOwnProfile } from './outreachExtension';
import { isSendersProfile } from './senderIdentity';

const KIND: Record<SenderFact['kind'], string> = { employer: 'Employer', school: 'University', field: 'Studied', interest: 'Interest' };
const errText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);

/** Why a read failed, in words a recruiter can act on. */
function readProblem(error: string | undefined, first: string) {
  if (error === 'not_logged_in') return `Sign in to LinkedIn as ${first} first.`;
  if (error === 'unknown_type') return 'Update the FINN Outreach Sender extension (version 0.7.1 or newer) and try again.';
  if (error === 'no_answer') return 'The FINN Outreach Sender extension did not answer. Check that it is installed and up to date.';
  return 'The profile could not be read. Try again.';
}

/** `onReading`: the dialog holds "Save" while a read runs (Sofia, Oct 27 2026: "woher weiß ich, dass ich nicht auf Save klicken soll?"). */
export function SenderFacts({
  email,
  name,
  linkedinUrl = null,
  onReading,
}: {
  email: string;
  name: string;
  linkedinUrl?: string | null;
  onReading?: (reading: boolean) => void;
}) {
  const first = name.split(' ')[0] || name;
  const [facts, setFacts] = useState<SenderFact[] | null>(null);
  const [busy, setBusy] = useState(false);
  // Signed in to LinkedIn as someone else than "Send as" (Sofia, Oct 27 2026): said in the box, nothing read.
  const [otherPerson, setOtherPerson] = useState<string | null>(null);

  useEffect(() => {
    // One instance per sender (the dialog keys it by email), so no reset is needed here.
    let alive = true;
    void api
      .fetchSenderProfile(email)
      .then((p) => alive && setFacts(p.facts))
      .catch(() => alive && setFacts([]));
    return () => {
      alive = false;
    };
  }, [email]);

  async function read() {
    setBusy(true);
    onReading?.(true);
    setOtherPerson(null);
    try {
      const r = await captureOwnProfile();
      if (!r.ok || !r.text) return showToast({ status: 'error', position: 'bottomCenter', description: readProblem(r.error, first) });
      // LinkedIn reads the signed-in person: it must be the sender picked here. No name read: no check, nothing kept.
      if (!r.name) return showToast({ status: 'error', position: 'bottomCenter', description: readProblem(undefined, first) });
      // A sender with a LinkedIn link needs the read's URL to compare: an older extension sends none.
      if (linkedinUrl && !r.publicUrl) return showToast({ status: 'error', position: 'bottomCenter', description: readProblem('unknown_type', first) });
      if (!isSendersProfile({ name, linkedinUrl }, r)) return setOtherPerson(r.name);
      const p = await api.readSenderProfile(email, r.text, r.pages);
      setFacts(p.facts);
      if (!p.facts.length) showToast({ status: 'warning', position: 'bottomCenter', description: 'No facts found in this profile.' });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not read the profile.') });
    } finally {
      setBusy(false);
      onReading?.(false);
    }
  }

  async function save(next: SenderFact[]) {
    const before = facts;
    setFacts(next);
    try {
      setFacts((await api.saveSenderFacts(email, next.map((f) => ({ id: f.id, on: f.on })))).facts);
    } catch (err) {
      setFacts(before);
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not save.') });
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-solid border-[#E9EAEC] p-3 text-[13px]" data-testid="sender-facts">
      <div className="flex items-center gap-2">
        <span className="flex-1 font-semibold">{first}’s profile</span>
        <Button size="sm" variant="outline" className="rounded-md" disabled={busy || facts === null} onClick={() => void read()}>
          {busy ? 'Reading…' : facts?.length ? 'Read again' : 'Read profile'}
        </Button>
      </div>
      <p className="m-0 text-xs text-[#707070]">
        Reads the profile of whoever is signed in to LinkedIn in this Chrome right now — that should be {first}. Untick what should not be used — changes here save right away.
      </p>
      {busy && (
        <p className="m-0 rounded-md bg-[#F3F8FE] px-3 py-2 text-xs text-[#1A1A1A]" role="status" data-testid="reading-profile">
          Reading the profile in the background — about 20 seconds. Wait before you save.
        </p>
      )}
      {otherPerson && (
        <p className="m-0 rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-3 py-2 text-xs text-[#7A4B00]" role="alert" data-testid="other-linkedin-person">
          LinkedIn is signed in as <b>{otherPerson}</b>, not {name}. Nothing was read. Sign in to LinkedIn as {first}, or pick {otherPerson.split(' ')[0]} under “Send as”.
        </p>
      )}
      {facts && facts.length > 0 && (
        <ul className="m-0 max-h-48 list-none space-y-1 overflow-y-auto p-0">
          {facts.map((f) => (
            <li key={f.id} className="flex items-center gap-2">
              <Checkbox
                id={`sender-fact-${f.id}`}
                className="shrink-0"
                checked={f.on}
                onCheckedChange={() => void save(facts.map((x) => (x.id === f.id ? { ...x, on: !x.on } : x)))}
                label={
                  <span title={f.names?.length ? `Also found as: ${f.names.join(', ')}` : undefined}>
                    {f.label}{' '}
                    <span className="text-xs text-[#707070]">
                      {KIND[f.kind]}
                      {f.from ? ` · ${f.from}–${f.to ?? 'today'}` : ''}
                    </span>
                  </span>
                }
              />
              <span className="flex-1" />
              <Button size="sm" variant="ghost" aria-label={`Delete ${f.label}`} onClick={() => void save(facts.filter((x) => x.id !== f.id))}>
                ✕
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
